import { FunctionComponent, ReactNode, MouseEvent } from 'react'

interface AllButtonProps {
  onClick?: (e?: MouseEvent) => void
  disabled?: boolean
  className?: string
  secondary?: boolean
  children?: ReactNode
}

interface ButtonProps {
  size?: 'large' | 'medium' | 'small'
  type?: 'button' | 'submit'
}

type ButtonCombinedProps = AllButtonProps & ButtonProps

const Button: FunctionComponent<ButtonCombinedProps> = ({
  children,
  onClick,
  disabled = false,
  className = '',
  secondary,
  size = 'medium',
  type = 'button',
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full text-sm ${
        secondary
          ? 'border-2 border-th-button md:hover:border-th-button-hover'
          : 'bg-th-button md:hover:bg-th-button-hover'
      } ${
        size === 'medium'
          ? 'h-10 px-4'
          : size === 'large'
            ? 'h-12 px-6'
            : 'h-8 px-3'
      } default-transition font-bold ${
        secondary ? 'text-th-fgd-1' : 'text-th-button-text'
      } disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:brightness-100 ${className}`}
      type={type}
      {...props}
    >
      {children}
    </button>
  )
}

interface IconButtonProps {
  hideBg?: boolean
  size?: 'small' | 'medium' | 'large'
}

type IconButtonCombinedProps = AllButtonProps & IconButtonProps

export const IconButton: FunctionComponent<IconButtonCombinedProps> = ({
  children,
  onClick,
  disabled = false,
  className = '',
  hideBg,
  size,
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex shrink-0 ${
        size === 'large'
          ? 'size-12'
          : size === 'small'
            ? 'size-8'
            : size === 'medium'
              ? 'size-10'
              : ''
      } default-transition items-center justify-center rounded-full ${
        hideBg ? 'md:hover:text-th-active' : 'bg-th-bkg-3 md:hover:bg-th-bkg-4'
      } text-th-fgd-1 focus:outline-none disabled:cursor-not-allowed disabled:bg-th-bkg-4 disabled:text-th-fgd-4 md:disabled:hover:text-th-fgd-4 ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

interface LinkButtonProps {
  icon?: ReactNode
}

type LinkButtonCombinedProps = AllButtonProps & LinkButtonProps

export const LinkButton: FunctionComponent<LinkButtonCombinedProps> = ({
  children,
  onClick,
  disabled = false,
  className = '',
  secondary,
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`default-transition flex items-center border-0 ${
        secondary
          ? 'text-th-active md:hover:text-th-active-dark'
          : 'text-th-fgd-2 md:hover:text-th-fgd-1'
      } focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
      type="button"
    >
      {children}
    </button>
  )
}

export default Button
